import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import ContactSalesForm from "@/components/ContactSalesForm";
import { 
  ArrowLeft, 
  Download,
  ExternalLink,
  CheckCircle2,
  Calculator,
  Phone
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductDetailPage = () => {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProduct();
  }, [productId]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${productId}`);
      setProduct(response.data);
    } catch (error) {
      console.error("Error fetching product:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await axios.get(`${API}/products/${productId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${product.name.replace(/\s+/g, '_')}_Spec_Sheet.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error downloading PDF:", error);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-[60vh]">
          <div className="spinner"></div>
        </div>
      </Layout>
    );
  }

  if (!product) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <h2 className="text-2xl font-bold text-slate-800 mb-4">Product Not Found</h2>
          <Link to="/products">
            <Button className="bg-[#00BCD4] hover:bg-[#00acc1]">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Products
            </Button>
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="py-8 md:py-12" data-testid="product-detail-page">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          {/* Header */}
          <Link 
            to="/products"
            className="inline-flex items-center text-[#00BCD4] hover:text-[#00acc1] font-medium mb-6"
            data-testid="back-to-products"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to All Products
          </Link>

          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            {/* Product Header */}
            <div className="bg-gradient-to-r from-[#0B1D2B] to-[#1a365d] p-6 md:p-8">
              <span className="text-xs font-bold tracking-wider text-[#00BCD4] uppercase mb-2 block">
                {product.category}
              </span>
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
                {product.name}
              </h1>
              <p className="text-slate-300 text-lg max-w-2xl">
                {product.description}
              </p>
            </div>

            {/* Content */}
            <div className="p-6 md:p-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Left Column */}
                <div>
                  {/* Dosage */}
                  <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-5 mb-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-xs font-bold tracking-wider text-slate-500 uppercase">
                          Recommended Dosage
                        </span>
                        <p className="text-3xl font-mono font-bold text-[#00BCD4]">
                          {product.dosage_range}
                        </p>
                      </div>
                      <Link to="/calculator">
                        <Button className="bg-[#00BCD4] hover:bg-[#00acc1]">
                          <Calculator className="w-4 h-4 mr-2" />
                          Calculate
                        </Button>
                      </Link>
                    </div>
                  </div>

                  {/* Applications */}
                  <div className="mb-6">
                    <h3 className="text-xs font-bold tracking-wider text-slate-400 uppercase mb-3">
                      Applications
                    </h3>
                    <div className="flex flex-wrap">
                      {product.applications?.map((app, i) => (
                        <span key={i} className="app-tag">{app}</span>
                      ))}
                    </div>
                  </div>

                  {/* Features */}
                  <div className="mb-6">
                    <h3 className="text-xs font-bold tracking-wider text-slate-400 uppercase mb-3">
                      Key Features
                    </h3>
                    <ul className="space-y-3">
                      {product.features?.map((feature, i) => (
                        <li key={i} className="flex items-start gap-3 text-slate-700">
                          <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Actions */}
                  <div className="space-y-3">
                    {/* <Button 
                      onClick={handleDownloadPDF}
                      className="w-full bg-[#00BCD4] hover:bg-[#00acc1] mb-4"
                      data-testid="download-pdf-btn"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download Spec Sheet (PDF)
                    </Button> */}
                    
                    {product.product_url && (
                      <a href={product.product_url} target="_blank" rel="noopener noreferrer" className="block">
                        <Button variant="" className="w-full bg-[#00BCD4] hover:bg-[#00acc1]">
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Visit Product Page
                        </Button>
                      </a>
                    )}
                    
                    <ContactSalesForm 
                      productInterest={product.name}
                      trigger={
                        <Button variant="" className="w-full bg-[#00BCD4] hover:bg-[#00acc1] mb-4">
                          <Phone className="w-4 h-4 mr-2" />
                          Contact Sales Team
                        </Button>
                      }
                    />
                  </div>
                </div>

                {/* Right Column */}
                <div>
                  {/* Specifications */}
                  <div className="bg-slate-50 rounded-lg p-5 mb-6">
                    <h3 className="text-xs font-bold tracking-wider text-slate-400 uppercase mb-4">
                      Technical Specifications
                    </h3>
                    <dl className="spec-list">
                      {Object.entries(product.specifications || {}).map(([key, value]) => (
                        <div key={key} className="py-3 border-b border-slate-200 last:border-0">
                          <dt>{key}</dt>
                          <dd className="font-mono">{value}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>

                  {/* Actions */}
                  <div className="space-y-3">
                    {/* <Button 
                      onClick={handleDownloadPDF}
                      className="w-full bg-[#00BCD4] hover:bg-[#00acc1] mb-4"
                      data-testid="download-pdf-btn"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download Spec Sheet (PDF)
                    </Button> 
                    
                    {product.product_url && (
                      <a href={product.product_url} target="_blank" rel="noopener noreferrer" className="block">
                        <Button variant="outline" className="w-full">
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Visit Product Page
                        </Button>
                      </a>
                    )}
                    
                    <ContactSalesForm 
                      productInterest={product.name}
                      trigger={
                        <Button variant="outline" className="w-full">
                          <Phone className="w-4 h-4 mr-2" />
                          Contact Sales Team
                        </Button>
                      }
                    />
                    */}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ProductDetailPage;
